import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../../../../../../backend/src/shared/action-registry';
import { useTogglePublishedKimThread } from '@/api/mutations/useTogglePublishedKimThreads';
import { useGetKimThread } from '@/api/queries/useGetKimThread';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';

export function SharePopover() {
  const { matterId, subsetId, threadId } = useParams();
  const { data: thread } = useGetKimThread(matterId, subsetId, threadId);
  const { mutate: toggleThreadPublished, isLoading: isTogglingThreadPublished } = useTogglePublishedKimThread(
    matterId,
    subsetId,
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="primary" size="sm" className="mt-px pr-2.5 hover:bg-teal-50">
          <Icon className="lucide--share-2" />
          Share
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-2" sideOffset={8} onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="space-y-0.5 text-xs text-gray-700">
          <PermissionGate permission={Permission.ThreadPublish} uiMode="disabled" sideOffset={14}>
            <button
              disabled={isTogglingThreadPublished}
              onClick={() => toggleThreadPublished(threadId!)}
              className="flex w-full items-start gap-2 rounded-sm px-2.5 py-2 text-left ring-teal-500 ring-offset-2 transition-[scale,background-color] duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-1 active:scale-99 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5 text-gray-900">
                  {isTogglingThreadPublished ? (
                    <Icon className="lucide--loader animate-spin" />
                  ) : (
                    <Icon className="lucide--users-round size-3" />
                  )}

                  {thread?.isPublished ? 'Unpublish thread' : 'Publish thread'}
                </div>
                <span className="mt-1.5 text-gray-400">
                  Publishing will make this thread appear in the 'Published Threads' list for all users in this Matter.
                </span>
              </div>
            </button>
          </PermissionGate>

          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              toast.success('Link copied to clipboard');
            }}
            className="flex w-full items-center gap-2 rounded-sm px-2.5 py-2 text-left ring-teal-500 ring-offset-2 transition-[scale,background-color] duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-1 active:scale-99"
          >
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5">
                <Icon className="lucide--link-2" />
                <span className="text-gray-900">Copy link to thread</span>
              </div>
              <span className="mt-1.5 text-gray-400">
                Any user with access to this Matter is able to view this thread.
              </span>
            </div>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
